import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Container, Divider, List, ListItem, ListItemText, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { db } from '../../firebase';
import { collection, query, where, getDocs, setDoc, doc, serverTimestamp, updateDoc } from 'firebase/firestore';

export default function PlaceOrder({ companyCode, tableId, setCart, unitCode }) {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [error, setError] = useState(null);
  const [localCart, setLocalCart] = useState(state?.cart || []);

  const totalAmount = state?.totalAmount || 0;
  const stateTableId = state?.tableId;
  const stateCompanyCode = state?.companyCode;
  const stateUnitCode = state?.unitCode;

  // Use props first, fallback to state
  const effectiveTableId = tableId || stateTableId;
  const effectiveCompanyCode = companyCode || stateCompanyCode;
  const effectiveUnitCode = unitCode || stateUnitCode;

  console.log('PlaceOrder: Received:', {
    cart: localCart.map(item => ({ id: item.id, name: item.name, quantity: item.quantity, totalPrice: item.totalPrice, cookingInstructions: item.cookingInstructions })),
    companyCode: effectiveCompanyCode,
    unitCode: effectiveUnitCode,
    tableId: effectiveTableId,
    totalAmount
  });

  const isCartValid = Array.isArray(localCart) && localCart.length > 0 && localCart.every(item => item.id && item.name && item.quantity > 0 && item.price >= 0);
  const isTotalValid = typeof totalAmount === 'number' && totalAmount >= 0;

  const generateOrderId = async () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 10; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }

    const ordersQuery = query(collection(db, 'orders'), where('orderId', '==', result));
    const snapshot = await getDocs(ordersQuery);
    if (!snapshot.empty) {
      return generateOrderId();
    }
    return `${effectiveCompanyCode}_${effectiveUnitCode}_${result}`; // Prefix with companyCode and unitCode for uniqueness
  };

  const generateItemInstanceId = () => {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const handlePlaceOrder = async () => {
    try {
      if (!isCartValid) {
        setError('Cannot place order: Cart is empty or contains invalid items.');
        console.error('PlaceOrder: Cart validation failed:', localCart);
        return;
      }
      if (!effectiveTableId) {
        setError('Cannot place order: Table ID is missing.');
        console.error('PlaceOrder: Table ID missing:', effectiveTableId);
        return;
      }
      if (!effectiveCompanyCode) {
        setError('Cannot place order: Company code is missing.');
        console.error('PlaceOrder: Company code missing:', effectiveCompanyCode);
        return;
      }
      if (!effectiveUnitCode) {
        setError('Cannot place order: Unit code is missing.');
        console.error('PlaceOrder: Unit code missing:', effectiveUnitCode);
        return;
      }

      let uniqueOrderId = localStorage.getItem('currentOrderId');
      let newTotalAmount = totalAmount;
      let itemsToSave = localCart.map(item => ({
        itemInstanceId: generateItemInstanceId(), // Unique ID for each item instance
        id: item.id, // Menu item ID
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        totalPrice: Number((item.totalPrice || item.quantity * item.price).toFixed(2)),
        status: 'Confirmed',
        cookingInstructions: (item.cookingInstructions || '').trim().slice(0, 100)
      }));

      console.log('PlaceOrder: itemsToSave before processing:', itemsToSave);

      // Fetch all menu items for the company and unit
      const menuQuery = query(
        collection(db, 'companymenu'),
        where('companyCode', '==', effectiveCompanyCode),
        where('unitCode', '==', effectiveUnitCode)
      );
      const menuSnapshot = await getDocs(menuQuery);
      const menuItemsMap = new Map();
      menuSnapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        menuItemsMap.set(docSnap.id, data); // Key is documentId
      });

      console.log('PlaceOrder: Menu items fetched:', Array.from(menuItemsMap.entries()).map(([id, data]) => ({ id, staff: data.staff })));

      const staffAssignments = new Map();
      for (const item of itemsToSave) {
        const menuItem = menuItemsMap.get(item.id); // item.id is the Firestore document ID
        const staffId = menuItem?.staff || 'default_kitchen_staff';
        staffAssignments.set(item.itemInstanceId, staffId); // Use itemInstanceId
        console.log(`Assigned staff ${staffId} for item ${item.name} (Item Instance ID: ${item.itemInstanceId}, Menu Item ID: ${item.id})`);
      }

      const itemsByStaff = new Map();
      itemsToSave.forEach(item => {
        const staffId = staffAssignments.get(item.itemInstanceId);
        if (!itemsByStaff.has(staffId)) {
          itemsByStaff.set(staffId, []);
        }
        itemsByStaff.get(staffId).push(item);
      });
      console.log('PlaceOrder: Items grouped by staff:', Array.from(itemsByStaff.entries()));

      const ordersQuery = query(
        collection(db, 'orders'),
        where('tableId', '==', effectiveTableId),
        where('companyCode', '==', effectiveCompanyCode),
        where('unitCode', '==', effectiveUnitCode)
      );
      const snapshot = await getDocs(ordersQuery);
      let latestOrder = null;

      if (!snapshot.empty) {
        latestOrder = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))[0];
        console.log('PlaceOrder: Latest order for tableId, companyCode, and unitCode:', { tableId: effectiveTableId, companyCode: effectiveCompanyCode, unitCode: effectiveUnitCode, latestOrder });
      }

      if (latestOrder && latestOrder.tableId === effectiveTableId && latestOrder.companyCode === effectiveCompanyCode && latestOrder.unitCode === effectiveUnitCode && !latestOrder.paid) {
        uniqueOrderId = latestOrder.orderId;
        console.log('PlaceOrder: Appending to existing unpaid order:', uniqueOrderId);

        // Append new items without merging items with different statuses
        const existingItems = latestOrder.items || [];
        itemsToSave = [
          ...existingItems,
          ...itemsToSave
        ];
        newTotalAmount = Number(itemsToSave.reduce((sum, item) => sum + item.totalPrice, 0).toFixed(2));

        // Update existing order with new items and total
        await updateDoc(doc(db, 'orders', uniqueOrderId), {
          items: itemsToSave,
          totalAmount: newTotalAmount,
          unitCode: effectiveUnitCode,
          updatedAt: serverTimestamp()
        });
      } else {
        uniqueOrderId = await generateOrderId();
        console.log('PlaceOrder: Generated new orderId:', uniqueOrderId);

        const orderData = {
          orderId: uniqueOrderId,
          tableId: effectiveTableId,
          companyCode: effectiveCompanyCode,
          unitCode: effectiveUnitCode,
          items: itemsToSave,
          totalAmount: newTotalAmount,
          status: 'Confirmed',
          paid: false,
          createdAt: serverTimestamp(),
        };

        await setDoc(doc(db, 'orders', uniqueOrderId), orderData);
        console.log('PlaceOrder: Order saved to Firestore:', { uniqueOrderId, orderData });
      }

      try {
        const tableDocId = `${effectiveCompanyCode}_${effectiveUnitCode}_${effectiveTableId}`;
       /* await setDoc(doc(db, 'tables', tableDocId), { currentOrderId: uniqueOrderId }, { merge: true });*/
       await setDoc(
  doc(db, 'tables', tableDocId),
  {
    currentOrderId: uniqueOrderId,
    tableStatus: "Reserved"
  },
  { merge: true }
);
        console.log('PlaceOrder: Updated tables collection:', { tableId: effectiveTableId, unitCode: effectiveUnitCode, currentOrderId: uniqueOrderId });
      } catch (tableError) {
        console.error('PlaceOrder: Error updating tables collection:', tableError);
        setError(`Failed to update table status: ${tableError.message}`);
      }

      // Send notifications to each assigned kitchen staff
      const notificationPromises = [];
      for (const [staffId, items] of itemsByStaff.entries()) {
        const notificationId = `kitchen_${uniqueOrderId}_${staffId}_${Date.now()}`;
        const itemsSummary = items.map(item => 
          `${item.name} (x${item.quantity})${item.cookingInstructions ? `, ${item.cookingInstructions}` : ''}`
        ).join('; ');
        const notificationData = {
          orderId: uniqueOrderId,
          tableId: effectiveTableId,
          companyCode: effectiveCompanyCode,
          unitCode: effectiveUnitCode,
          userId: staffId,
          message: `New order for Table ${effectiveTableId} (Order ID: ${uniqueOrderId}, Unit: ${effectiveUnitCode}): ${itemsSummary}`,
          role: 'kitchen_staff',
          createdAt: serverTimestamp(),
          read: false,
        };
        console.log('PlaceOrder: Preparing notification:', { notificationId, notificationData });
        notificationPromises.push(
          setDoc(doc(db, 'notifications', notificationId), notificationData)
            .then(() => {
              console.log('PlaceOrder: Notification sent to:', { notificationId, userId: staffId });
            })
            .catch(notificationError => {
              console.error(`PlaceOrder: Error creating notification ${notificationId}:`, notificationError);
              throw notificationError;
            })
        );
      }

      await Promise.all(notificationPromises);
      console.log('PlaceOrder: All notifications sent to assigned kitchen staff:', notificationPromises.length);

      localStorage.setItem('currentOrderId', uniqueOrderId);
      console.log('PlaceOrder: Stored in localStorage:', localStorage.getItem('currentOrderId'));

      // Clear local and global cart
      console.log('PlaceOrder: Clearing local cart, current cart:', localCart);
      setLocalCart([]);
      console.log('PlaceOrder: Local cart cleared, new cart:', []);
      if (setCart) {
        console.log('PlaceOrder: Clearing global cart');
        setCart([]);
        console.log('PlaceOrder: Global cart cleared');
      }

      setOrderId(uniqueOrderId);
      setOrderConfirmed(true);
    } catch (error) {
      console.error('PlaceOrder: Error saving order or sending notifications:', error);
      setError(`Failed to place order or send notifications: ${error.message}`);
    }
  };

  const handleBackToMenu = () => {
    console.log('PlaceOrder: Navigating to menu:', { companyCode: effectiveCompanyCode, unitCode: effectiveUnitCode, tableId: effectiveTableId });
    navigate(`/customer/menu?code=${effectiveCompanyCode}&unit=${effectiveUnitCode}&id=${effectiveTableId}`);
  };

  const handleTrackOrder = () => {
    console.log('PlaceOrder: Navigating to track-order:', { orderId, companyCode: effectiveCompanyCode, unitCode: effectiveUnitCode, tableId: effectiveTableId });
    navigate(`/customer/track-order?code=${effectiveCompanyCode}&unit=${effectiveUnitCode}&id=${effectiveTableId}`, {
      state: {
        orderId,
        cart: localCart,
        totalAmount,
        tableId: effectiveTableId,
        companyCode: effectiveCompanyCode,
        unitCode: effectiveUnitCode,
        status: 'Confirmed'
      }
    });
  };

  const handleRequestBill = () => {
    console.log('PlaceOrder: Navigating to request-bill:', { companyCode: effectiveCompanyCode, unitCode: effectiveUnitCode, tableId: effectiveTableId });
    navigate(`/customer/bill?code=${effectiveCompanyCode}&unit=${effectiveUnitCode}&id=${effectiveTableId}`, {
      state: {
        cart: localCart,
        totalAmount,
        tableId: effectiveTableId,
        companyCode: effectiveCompanyCode,
        unitCode: effectiveUnitCode
      }
    });
  };

  useEffect(() => {
    console.log('PlaceOrder: Initial localCart:', localCart, 'State cart:', state?.cart);
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Place Your Order
      </Typography>

      {error && (
        <Typography variant="h6" color="error" sx={{ textAlign: 'center', mb: 3 }}>
          {error}
        </Typography>
      )}

      {orderConfirmed ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <CheckCircleIcon sx={{ fontSize: '4rem', color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Your order has been placed successfully!
          </Typography>
          <Typography variant="body1" gutterBottom>
            Order ID: {orderId || 'N/A'}
          </Typography>
          <List sx={{ mb: 3 }}>
            {localCart.map((item) => (
              <ListItem key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <ListItemText
                  primary={item.name}
                  secondary={`Quantity: ${item.quantity}${item.cookingInstructions ? `, Instructions: ${item.cookingInstructions}` : ''}`}
                  primaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                  secondaryTypographyProps={{ variant: 'body2' }}
                />
                <Typography variant="body1">
                  ₹{(item.totalPrice || item.quantity * item.price).toFixed(2)}
                </Typography>
              </ListItem>
            ))}
          </List>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleTrackOrder}
            >
              Track Order
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleRequestBill}
            >
              Request Bill
            </Button>
          </Box>
        </Box>
      ) : !isCartValid || !effectiveTableId || !effectiveCompanyCode || !effectiveUnitCode ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <Typography variant="h6" color="text.secondary">
            {!isCartValid
              ? 'Your cart is empty or contains invalid items.'
              : !effectiveTableId
              ? 'Table ID is missing.'
              : !effectiveCompanyCode
              ? 'Company code is missing.'
              : 'Unit code is missing.'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleBackToMenu}
          >
            Back to Menu
          </Button>
        </Box>
      ) : (
        <>
          <Typography variant="h6" gutterBottom>
            Order Summary
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List>
            {localCart.map((item) => (
              <ListItem key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <ListItemText
                  primary={item.name}
                  secondary={`Quantity: ${item.quantity}${item.cookingInstructions ? `, Instructions: ${item.cookingInstructions}` : ''}`}
                  primaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                  secondaryTypographyProps={{ variant: 'body2' }}
                />
                <Typography variant="body1">
                  ₹{(item.totalPrice || item.quantity * item.price).toFixed(2)}
                </Typography>
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Total Amount:
            </Typography>
            <Typography variant="h6" color="primary">
              {isTotalValid ? `₹${totalAmount.toFixed(2)}` : '₹0'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleBackToMenu}
            >
              Back to Menu
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePlaceOrder}
            >
              Confirm Order
            </Button>
          </Box>
        </>
      )}
    </Container>
  );
}